import type { FamilyMember, MemberLocation } from '../../../shared/types';
import Avatar from '../../../components/Avatar';
import type { Resource } from '../useResource';
import { relativeTime } from '../dates';
import { Card, CardTitle, Empty, ErrorState, Icon, LoadingRows } from '../ui';

/**
 * Where everyone is. Life360 is unofficial and can go quiet, so this always shows
 * *last known* with an age on it — a stale fix, honestly labelled, beats a card
 * that just disappears. Adults only; the kid view never mounts it.
 *
 * The names and colors come from the roster, so a roster failure is a card
 * failure: rendering dots with no names would be worse than saying so.
 */
export default function WhereCard({
  res,
  family,
}: {
  res: Resource<MemberLocation[]>;
  family: Resource<FamilyMember[]>;
}) {
  const busy = res.loading || family.loading;
  const error = res.error ?? family.error;
  const retry = () => {
    if (res.error) res.reload();
    if (family.error) family.reload();
  };

  const members = (family.data ?? []).filter((m) => m.active);
  const locations = res.data ?? [];

  return (
    <Card className="p-5">
      <CardTitle icon="pin" action={{ to: '/location', label: 'Map' }}>
        Where everyone is
      </CardTitle>

      <div className="mt-4">
        {busy && <LoadingRows rows={2} />}
        {!busy && error && <ErrorState what="everyone's location" onRetry={retry} />}

        {!busy && !error && members.length === 0 && (
          <Empty
            message="No one's in the household yet. Add the family and everyone shows up here."
            action={{ to: '/admin', label: 'Add family members' }}
          />
        )}

        {!busy && !error && members.length > 0 && locations.length === 0 && (
          <Empty
            message="No locations yet. Turn on sharing for each phone and everyone's last known spot lands here."
            action={{ to: '/location', label: 'Set up sharing' }}
          />
        )}

        {!busy && !error && members.length > 0 && locations.length > 0 && (
          <ul className="space-y-3">
            {members.map((member) => {
              const location = locations.find((l) => l.member_id === member.id) ?? null;
              return (
                <li key={member.id} className="flex items-center gap-3">
                  <Avatar
                    name={member.name}
                    color={member.color}
                    url={
                      member.avatar_key
                        ? `/api/location/${member.id}/avatar?v=${encodeURIComponent(member.avatar_key)}`
                        : null
                    }
                    className="h-8 w-8"
                    textClassName="text-xs"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{member.name}</p>
                    <p className="truncate text-sm text-ink-soft">
                      {location
                        ? `${location.place_name ?? `${location.lat.toFixed(3)}, ${location.lng.toFixed(3)}`} · ${relativeTime(location.recorded_at)}`
                        : 'No location shared yet'}
                    </p>
                  </div>
                  {location?.is_driving && (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-dusk-soft px-2 py-0.5 text-xs font-medium text-dusk">
                      <Icon name="car" className="h-3.5 w-3.5" />
                      Driving
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}
